import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  tableCellClasses,
  LinearProgress,
  CircularProgress,
  Badge,
  Drawer,
  Breadcrumbs,
} from '@mui/material';
import {
  Add,
  AddCircleOutline,
  AttachMoney,
  Category,
  CheckCircle,
  Close,
  Delete,
  DeleteOutline,
  Discount,
  Download,
  Edit,
  FilterList,
  Inventory,
  Link,
  LocalShipping,
  MoreVert,
  Publish,
  Public,
  Refresh,
  RemoveRedEye,
  Save,
  Search,
  ShoppingCart,
  Star,
  Store,
  Upload,
  Visibility,
  Warning,
  Image as ImageIcon,
  TextSnippet,
  LocalOffer,
  SupervisorAccount,
  DragIndicator,
  VideocamOutlined,
  ColorLens,
  FormatSize,
  List as ListItemIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { blue, green, red, orange, grey } from '@mui/material/colors';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import pb from './pocketbase';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
    color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
  },
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
    cursor: 'pointer',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const ProductImage = styled(Box)(({ theme }) => ({
  width: 50,
  height: 50,
  borderRadius: theme.shape.borderRadius,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.default,
}));

const StatusChip = styled(Chip)(({ status, theme }) => ({
  fontWeight: 600,
  ...(status === 'published' && {
    backgroundColor: green[100],
    color: green[800],
  }),
  ...(status === 'draft' && {
    backgroundColor: orange[100],
    color: orange[800],
  }),
  ...(status === 'archived' && {
    backgroundColor: grey[300],
    color: grey[800],
  }),
  ...(status === 'out_of_stock' && {
    backgroundColor: red[100],
    color: red[800],
  }),
}));

const UploadZone = styled(Box)(({ theme, isDragging }) => ({
  border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(5, 2),
  textAlign: 'center',
  backgroundColor: isDragging ? `${theme.palette.primary.main}10` : theme.palette.background.default,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
}));

const ImagePreview = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  width: '100%',
  height: 150,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    '& .image-actions': {
      opacity: 1,
    },
  },
}));

const ImageActions = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  opacity: 0,
  transition: 'all 0.2s ease',
}));

const StatCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  padding: theme.spacing(2),
}));

const StatIconWrapper = styled(Box)(({ theme, color }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 56,
  height: 56,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: color ? `${color}15` : `${theme.palette.primary.main}15`,
  color: color || theme.palette.primary.main,
  marginRight: theme.spacing(2),
}));

const FilterButton = styled(Button)(({ theme, active }) => ({
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  ...(active && {
    backgroundColor: `${theme.palette.primary.main}20`,
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main}30`,
    },
  }),
}));

// Mock data and utility functions
const generateMockProducts = (count) => {
  const statuses = ['published', 'draft', 'archived', 'out_of_stock'];
  const categories = [
    { id: 'cat1', name: 'Electronics' },
    { id: 'cat2', name: 'Clothing' },
    { id: 'cat3', name: 'Home & Kitchen' },
    { id: 'cat4', name: 'Beauty & Personal Care' },
    { id: 'cat5', name: 'Books' },
  ];
  
  const sellers = [
    { id: 'seller1', name: 'Tech Gadgets Store', shop: 'Tech Gadgets' },
    { id: 'seller2', name: 'Fashion Outlet', shop: 'Fashion Trends' },
    { id: 'seller3', name: 'Home Essentials', shop: 'Home Decor & More' },
    { id: 'seller4', name: 'Beauty Supply Co', shop: 'Beauty Emporium' },
    { id: 'seller5', name: 'Book Corner', shop: 'Book Haven' },
  ];
  
  return Array.from({ length: count }, (_, index) => {
    const id = `prod_${index + 1}`;
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const category = categories[Math.floor(Math.random() * categories.length)];
    const seller = sellers[Math.floor(Math.random() * sellers.length)];
    const basePrice = Math.floor(Math.random() * 200) + 10;
    const comparePrice = Math.random() > 0.5 ? basePrice + Math.floor(Math.random() * 100) : null;
    
    // Generate a product title
    const adjectives = ['Premium', 'Luxury', 'Essential', 'Modern', 'Classic', 'Professional', 'Handcrafted'];
    const productTypes = ['Headphones', 'Smartphone', 'T-Shirt', 'Watch', 'Camera', 'Laptop', 'Backpack', 'Sneakers'];
    const brands = ['TechPro', 'StyleX', 'HomeHub', 'GlowGuru', 'ReadMore'];
    
    const title = `${adjectives[Math.floor(Math.random() * adjectives.length)]} ${productTypes[Math.floor(Math.random() * productTypes.length)]} - ${brands[Math.floor(Math.random() * brands.length)]}`;
    
    // Generate a slug from the title
    const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    
    // Random description
    const description = `This ${title} is perfect for everyday use. It features high-quality materials and modern design. Available in multiple colors and sizes.`;
    
    // Generate random tags
    const tagOptions = ['new', 'bestseller', 'sale', 'trending', 'limited', 'premium'];
    const tags = JSON.stringify(Array.from({ length: Math.floor(Math.random() * 3) + 1 }, () => 
      tagOptions[Math.floor(Math.random() * tagOptions.length)]
    ));
    
    // Generate random variants
    const variantOptions = [
      { name: 'Color', options: ['Red', 'Blue', 'Black', 'White', 'Green'] },
      { name: 'Size', options: ['S', 'M', 'L', 'XL', 'XXL'] },
      { name: 'Material', options: ['Cotton', 'Polyester', 'Leather', 'Metal', 'Plastic'] }
    ];
    
    const variants = JSON.stringify(Array.from({ length: Math.floor(Math.random() * 2) + 1 }, (_, i) => {
      const variant = variantOptions[i];
      return {
        name: variant.name,
        options: Array.from({ length: Math.floor(Math.random() * 3) + 2 }, () => 
          variant.options[Math.floor(Math.random() * variant.options.length)]
        )
      };
    }));
    
    // Generate JSON representation of the category
    const categoryJson = JSON.stringify({
      id: category.id,
      name: category.name
    });
    
    return {
      id,
      collectionId: "ssay8ev6fmxsdh2",
      collectionName: "products",
      created: new Date(Date.now() - Math.random() * 10000000000).toISOString().replace('T', ' ').split('.')[0] + '.123Z',
      updated: new Date(Date.now() - Math.random() * 1000000000).toISOString().replace('T', ' ').split('.')[0] + '.456Z',
      title,
      slug,
      description,
      price: basePrice,
      compare_price: comparePrice,
      images: Array.from({ length: Math.floor(Math.random() * 4) + 1 }, (_, i) => `product_${id}_image_${i+1}.jpg`),
      video: Math.random() > 0.7 ? "https://example.com/video.mp4" : null,
      stock: Math.floor(Math.random() * 100),
      status,
      featured: Math.random() > 0.8,
      tags,
      variants,
      shop: seller.shop,
      category: categoryJson,
      delivery_charge: Math.floor(Math.random() * 10) + 5,
      rating: Math.random() * 5,
      reviews_count: Math.floor(Math.random() * 100),
      seller: seller.id,
      reviews: Array.from({ length: Math.floor(Math.random() * 5) }, (_, i) => `review_${id}_${i+1}`)
    };
  });
};

const Products = () => {
  // Navigation hook
  const navigate = useNavigate();
  
  // States
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalProducts, setTotalProducts] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [currentProductId, setCurrentProductId] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [filters, setFilters] = useState({
    status: 'all', // all, published, draft, archived, out_of_stock
    seller: 'all',
    category: 'all',
    sortBy: 'updated_desc' // updated_desc, updated_asc, price_desc, price_asc, name_asc, name_desc
  });
  const [categories, setCategories] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [statistics, setStatistics] = useState({
    total: 0,
    published: 0,
    draft: 0,
    archived: 0,
    outOfStock: 0,
    featured: 0
  });
  
  // Form states for edit/add product
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    price: '',
    compare_price: '',
    stock: '',
    status: 'draft',
    featured: false,
    delivery_charge: '',
    seller: '',
    category: '',
    tags: [],
    variants: [],
    digital_product: false,
    download_link: '',
  });
  const [productImages, setProductImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [digitalProductFile, setDigitalProductFile] = useState(null);
  const [digitalProductFileName, setDigitalProductFileName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [isAddingVariant, setIsAddingVariant] = useState(false);
  const [newVariant, setNewVariant] = useState({ name: '', options: [] });
  const [newVariantOption, setNewVariantOption] = useState('');
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  
  // Current UTC time from props
  const [currentDateTime] = useState('2025-04-02 12:18:54');
  const [currentUser] = useState('MdAbuNafisNiloy');
  

  useEffect(() => {
    const fetchAllProducts = async () => { 
        const products = await pb.collection('products').getFullList(); // ✅ Fix: 'const' added
        //  set statistics
        const stats = {
            total: products.length,
            published: products.filter(product => product.status === 'published').length,
            draft: products.filter(product => product.status === 'draft').length,
            archived: products.filter(product => product.status === 'archived').length,
            outOfStock: products.filter(product => product.stock === 0).length,
            featured: products.filter(product => product.featured).length
            };
            setStatistics(stats);
        };
        fetchAllProducts();
    }, []);




  // Fetch initial data
  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchSellers();
  }, []);
  
  // Fetch products when filters, pagination, or search changes
  useEffect(() => {
    fetchProducts();
  }, [page, rowsPerPage, filters, searchQuery]);
  
  // Calculate active filter count
  useEffect(() => {
    let count = 0;
    if (filters.status !== 'all') count++;
    if (filters.seller !== 'all') count++;
    if (filters.category !== 'all') count++;
    if (filters.sortBy !== 'updated_desc') count++;
    setActiveFilterCount(count);
  }, [filters]);
  
// Add this helper function to safely parse JSON
const safeJsonParse = (str) => {
    try {
      return JSON.parse(str);
    } catch (e) {
      console.error('Failed to parse JSON:', str);
      return null;
    }
  };
  
  
  // Function to fetch products
 // Function to fetch products from PocketBase
 const fetchProducts = async () => {
  try {
    setLoading(true);
    
    // Log filter application
    console.log(`[${currentDateTime}] User ${currentUser} fetching products with filters:`, filters);
    
    // Build filter string with proper PocketBase syntax
    let filterParts = [];
    
    // Category filter
    if (filters.category !== 'all') {
      // Check if category is stored as JSON or direct ID
      filterParts.push(`(category = "${filters.category}" || category ~ "${filters.category}")`);
    }
    
    // Status filter
    if (filters.status !== 'all') {
      if (filters.status === 'out_of_stock') {
        filterParts.push(`stock = 0`);
      } else {
      filterParts.push(`status = "${filters.status}"`);
      }
    }
    
    // Seller filter
    if (filters.seller !== 'all') {
      filterParts.push(`seller = "${filters.seller}"`);
    }
    
    // Search query filter (search in title, description, and shop)
    if (searchQuery.trim()) {
      filterParts.push(`(title ~ "${searchQuery}" || description ~ "${searchQuery}" || shop ~ "${searchQuery}")`);
    }
    
    // Combine all filters with AND operator
    const filterStr = filterParts.length > 0 ? filterParts.join(' && ') : '';
    
    // Determine sort direction based on the selected sort option
    let sortStr = '-created'; // default sort
    
    switch (filters.sortBy) {
      case 'updated_desc':
        sortStr = '-updated';
        break;
      case 'updated_asc':
        sortStr = 'updated';
        break;
      case 'price_desc':
        sortStr = '-price';
        break;
      case 'price_asc':
        sortStr = 'price';
        break;
      case 'name_asc':
        sortStr = 'title';
        break;
      case 'name_desc':
        sortStr = '-title';
        break;
      default:
        sortStr = '-updated';
    }
    
    console.log(`Applying filter: ${filterStr || 'none'}`);
    console.log(`Applying sort: ${sortStr}`);
    
    // Fetch with expanded relations
    const result = await pb.collection('products').getList(page + 1, rowsPerPage, {
      filter: filterStr,
      sort: sortStr,
      expand: 'category,seller'
    });
    
    // Process items to ensure consistent data format
    const processedItems = result.items.map(item => ({
      ...item,
      category: item.category
    }));
    
    setProducts(processedItems);
    setTotalProducts(result.totalItems);
    
    // Update statistics based on the current result
    const stats = {
      total: result.totalItems,
      published: result.items.filter(item => item.status === 'published').length,
      draft: result.items.filter(item => item.status === 'draft').length,
      archived: result.items.filter(item => item.status === 'archived').length,
      outOfStock: result.items.filter(item => item.status === 'out_of_stock').length,
      featured: result.items.filter(item => item.featured).length
    };
    
    // Only update statistics if we're on the first page with no filters
    if (page === 0 && Object.values(filters).every(val => val === 'all') && !searchQuery) {
      setStatistics(stats);
    }
    
    setLoading(false);
    
  } catch (err) {
    console.error(`[${currentDateTime}] Error fetching products:`, err);
    setError('Failed to load products. Please try again later.');
    setLoading(false);
    
    // Show error in snackbar
    setSnackbar({
      open: true,
      message: err.message || 'Failed to load products',
      severity: 'error'
    });
  }
};
  
  // Fetch categories
  const fetchCategories = async () => {
    try {
      // In a real app, this would call PocketBase API
      // For this example, we'll use mock data
      const mockCategories = await pb.collection('categories').getFullList(); 
      setCategories(mockCategories);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };
  
  // Fetch sellers
  const fetchSellers = async () => {
    try {
      const mockSellers = await pb.collection('sellers').getFullList();
      console.log(mockSellers);
      setSellers(mockSellers);
    } catch (err) {
      console.error('Error fetching sellers:', err);
    }
  };



  // Helper function to safely handle category data that could be a JSON string or a direct string ID
const getCategoryData = (categoryField) => {
    if (!categoryField) return { id: '', name: 'Unknown' };
    if (typeof categoryField === 'string') {
       try {
        const parsed = JSON.parse(categoryField);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      } catch (e) {
        // Not a valid JSON string, it's a direct category ID
        return { id: categoryField, name: getCategoryName(categoryField) };
      }
    }

     const categoryName= getCategoryName(categoryField[0]);
    console.log(categoryName);
    // If it's already an object, return it directly
    return  { id: categoryField[0], name: categoryName };
  };
  
  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle search
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };
  
  // Handle filter menu
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };
  
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };
  
  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    // Skip update if the value is the same (prevents unnecessary re-renders)
    if (filters[filterType] === value) return;
    
    // Validate filter type
    const validFilterTypes = ['status', 'seller', 'category', 'sortBy'];
    if (!validFilterTypes.includes(filterType)) {
      console.error(`Invalid filter type: ${filterType}`);
      return;
    }
    
    // Log filter change for auditing (with timestamp and user)
    console.log(`[${currentDateTime}] User ${currentUser} changed filter ${filterType} to ${value}`);
    
    // Special handling for category filter
    if (filterType === 'category') {
      // Store previous filter state in case we need to revert
      const previousFilters = {...filters};
      
      // Update filters state
      setFilters(prev => ({
        ...prev,
        [filterType]: value
      }));
      
      // Reset pagination
      setPage(0);
      
      // If filtering by category, update the filter string for PocketBase
      try {
        // Mark UI as loading
        setLoading(true);
        
        // Fetch filtered data (the fetchProducts function will use the updated filters)
        fetchProducts().catch(err => {
          console.error("Error applying category filter:", err);
          // Revert to previous filters if there's an error
          setFilters(previousFilters);
          setSnackbar({
            open: true,
            message: "Failed to apply filter. Please try again.",
            severity: "error"
          });
        });
      } catch (error) {
        console.error("Error in filter handling:", error);
      }
    } else {
      // For other filters, simply update the state
      setFilters(prev => ({
        ...prev,
        [filterType]: value
      }));
      
      // Reset pagination
      setPage(0);
    }
  };
  
  // Handle row click to view product
  const handleRowClick = (product) => {
    setSelectedProduct(product);
    setViewModalOpen(true);
  };
  
  // Handle action menu
  const handleActionMenuOpen = (event, productId) => {
    event.stopPropagation();
    setActionMenuAnchorEl(event.currentTarget);
    setCurrentProductId(productId);
  };
  
  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
    setCurrentProductId(null);
  };
  
  // Handle status change for a product
  const handleStatusChange = async (productId, newStatus) => {
    try {
      // In a real app, this would update the status in PocketBase
      // For this example, update the local state
      setProducts(products.map(product => 
        product.id === productId 
          ? { ...product, status: newStatus } 
          : product
      ));
      
      setSnackbar({
        open: true,
        message: `Product status updated to ${newStatus}`,
        severity: 'success'
      });
      
      handleActionMenuClose();
    } catch (error) {
      console.error('Error updating product status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update product status',
        severity: 'error'
      });
    }
  };
  
  // Handle featured toggle
  const handleFeaturedToggle = async (productId, currentFeatured) => {
    try {
      // In a real app, this would update the product in PocketBase
      // For this example, update the local state
      setProducts(products.map(product => 
        product.id === productId 
          ? { ...product, featured: !currentFeatured } 
          : product
      ));
      
      setSnackbar({
        open: true,
        message: currentFeatured 
          ? 'Product removed from featured' 
          : 'Product added to featured',
        severity: 'success'
      });
      
      handleActionMenuClose();
    } catch (error) {
      console.error('Error updating product featured status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update product featured status',
        severity: 'error'
      });
    }
  };
  
  // Handle product deletion
  const handleDeleteProduct = async (productId) => {
    try {
      // In a real app, this would delete the product in PocketBase
      // For this example, update the local state
      setProducts(products.filter(product => product.id !== productId));
      
      setSnackbar({
        open: true,
        message: 'Product deleted successfully',
        severity: 'success'
      });
      
      handleActionMenuClose();
    } catch (error) {
      console.error('Error deleting product:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete product',
        severity: 'error'
      });
    }
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Handle edit product
  const handleEditClick = (event, product) => {
    event.stopPropagation();
    
    // Parse tags and variants from JSON string to array
    const parsedTags = product.tags ? product.tags : [];
    const parsedVariants = product.variants ?  product.variants : [];
    
    setSelectedProduct(product);
    setEditForm({
      title: product.title,
      description: product.description,
      price: product.price,
      compare_price: product.compare_price || '',
      stock: product.stock,
      status: product.status,
      featured: product.featured,
      delivery_charge: product.delivery_charge,
      seller: product.seller,
      category: typeof product.category === 'string' && product.category.startsWith('{')
  ? getCategoryData(product.category).id
  : product.category,
      tags: parsedTags,
      variants: parsedVariants,
      digital_product: product.digital_product || false,
      download_link: product.download_link || '',
    });
    
    // Set product images using mock URLs
    const productImageUrls = product.images.map((image, index) => 
       pb.files.getURL(product,image)
    );
    console.log(productImageUrls);
    
    setProductImages(productImageUrls);
    setVideoUrl(product.video || '');
    
    setEditModalOpen(true);
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Handle image upload
  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length > 0) {
      // Store the file objects for later upload
      setImageFiles(prev => [...prev, ...files]);
      
      // Create URLs for preview
      const newImageUrls = files.map(file => URL.createObjectURL(file));
      setProductImages(prev => [...prev, ...newImageUrls]);
    }
  };
  
  // Handle image drop
  const handleImageDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(event.dataTransfer.files);
    
    if (files.length > 0) {
      // Filter for only image files
      const imageFiles = files.filter(file => 
        file.type.startsWith('image/')
      );
      
      if (imageFiles.length === 0) {
        setSnackbar({
          open: true,
          message: 'Please drop only image files',
          severity: 'warning'
        });
        return;
      }
      
      // Check for file size (limit to 5MB per file)
      const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
      const oversizedFiles = imageFiles.filter(file => file.size > MAX_FILE_SIZE);
      
      if (oversizedFiles.length > 0) {
        setSnackbar({
          open: true,
          message: `${oversizedFiles.length} file(s) exceed the 5MB size limit`,
          severity: 'warning'
        });
        return;
      }
      
      // Check for total number of images (limit to 10 total)
      const MAX_IMAGES = 10;
      if (productImages.length + imageFiles.length > MAX_IMAGES) {
        setSnackbar({
          open: true,
          message: `You can upload a maximum of ${MAX_IMAGES} images`,
          severity: 'warning'
        });
        return;
      }
      
      // Create URLs for preview
      const newImageUrls = imageFiles.map(file => URL.createObjectURL(file));
      
      // Update state with new files and URLs
      setImageFiles(prev => [...prev, ...imageFiles]);
      setProductImages(prev => [...prev, ...newImageUrls]);
      
      // Log for debugging
      console.log(`Added ${imageFiles.length} new images via drop. Total: ${productImages.length + newImageUrls.length}`);
    }
  };
  
  // Handle image deletion
  const handleImageDelete = (index) => {
    const newImages = [...productImages];
    newImages.splice(index, 1);
    setProductImages(newImages);
    
    const newFiles = [...imageFiles];
    if (index < newFiles.length) {
      newFiles.splice(index, 1);
      setImageFiles(newFiles);
    }
  };
  
  // Handle image reordering via drag and drop
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const reorderedImages = Array.from(productImages);
    const [reorderedItem] = reorderedImages.splice(result.source.index, 1);
    reorderedImages.splice(result.destination.index, 0, reorderedItem);
    
    setProductImages(reorderedImages);
    
    // Reorder image files too if they exist
    if (imageFiles.length > 0) {
      const reorderedFiles = Array.from(imageFiles);
      if (result.source.index < reorderedFiles.length && result.destination.index < reorderedFiles.length) {
        const [reorderedFile] = reorderedFiles.splice(result.source.index, 1);
        reorderedFiles.splice(result.destination.index, 0, reorderedFile);
        setImageFiles(reorderedFiles);
      }
    }
  };
  
  // Handle digital product file upload
  const handleDigitalProductUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Store the file object for later upload
      setDigitalProductFile(file);
      setDigitalProductFileName(file.name);
      
      // If a download link was provided, clear it as we now have a file
      if (editForm.download_link) {
        setEditForm(prev => ({
          ...prev,
          download_link: ''
        }));
      }
    }
  };
  
  // Handle download link change
  const handleDownloadLinkChange = (e) => {
    const link = e.target.value;
    
    // If a digital product file was uploaded, clear it as we now have a link
    if (digitalProductFile) {
      setDigitalProductFile(null);
      setDigitalProductFileName('');
    }
    
    setEditForm(prev => ({
      ...prev,
      download_link: link
    }));
  };
  
  // Clear digital product file
  const handleClearDigitalProduct = () => {
    setDigitalProductFile(null);
    setDigitalProductFileName('');
  };
  
  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!editForm.title.trim()) errors.title = 'Title is required';
    if (!editForm.description.trim()) errors.description = 'Description is required';
    if (!editForm.price) errors.price = 'Price is required';
    if (isNaN(parseFloat(editForm.price))) errors.price = 'Price must be a number';
    if (editForm.compare_price && isNaN(parseFloat(editForm.compare_price))) {
      errors.compare_price = 'Compare price must be a number';
    }
    if (!editForm.stock) errors.stock = 'Stock is required';
    if (isNaN(parseInt(editForm.stock))) errors.stock = 'Stock must be a number';
    if (!editForm.seller) errors.seller = 'Seller is required';
    if (!editForm.category) errors.category = 'Category is required';
    if (productImages.length === 0) errors.images = 'At least one product image is required';
    
    // Validate digital product
    if (editForm.digital_product) {
      // Check if both download link and file are provided or both are empty
      if ((editForm.download_link && digitalProductFile) || 
          (!editForm.download_link && !digitalProductFile && 
           (!selectedProduct || !selectedProduct.product))) {
        errors.digital_product = 'Please provide either a download link OR a digital product file, not both';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) return;
  
    try {
      setIsSubmitting(true);
      
      // For debugging
      console.log("Original product:", selectedProduct);
      console.log("Current images in UI:", productImages);
      console.log("New image files:", imageFiles);
      
      const slug = editForm.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      
      // STEP 1: Create data object without images first
      const data = {
        title: editForm.title,
        slug: slug,
        description: editForm.description,
        price: editForm.price,
        compare_price: editForm.compare_price || '',
        stock: editForm.stock,
        status: editForm.status,
        featured: editForm.featured,
        delivery_charge: editForm.delivery_charge || '',
        seller: editForm.seller,
        video: videoUrl || '',
        digital_product: editForm.digital_product,
        download_link: editForm.digital_product ? editForm.download_link : '',
        tags: Array.isArray(editForm.tags) ? JSON.stringify(editForm.tags) : editForm.tags,
        variants: Array.isArray(editForm.variants) ? JSON.stringify(editForm.variants) : editForm.variants,
        updated: '2025-05-09 12:00:00'
      };
      
      // Handle category field (same as before)
      if (typeof editForm.category === 'object') {
        data.category = editForm.category.id;
      } else if (typeof editForm.category === 'string') {
        if (editForm.category.startsWith('{')) {
          try {
            const categoryObj = JSON.parse(editForm.category);
            data.category = categoryObj.id;
          } catch (e) {
            data.category = editForm.category;
          }
        } else {
          data.category = editForm.category;
        }
      } else {
        data.category = editForm.category || '';
      }
      
      // STEP 2: Determine which images to keep and which to upload
      // Extract just the filenames from URLs (this is the key fix)
      const originalImageFilenames = selectedProduct?.images || [];
      
      // Extract filenames from UI image URLs
      const keptImageFilenames = [];
      const blobImageIndexes = [];
      
      productImages.forEach((imageUrl, index) => {
        if (imageUrl.startsWith('blob:')) {
          // Mark this index as a blob URL that needs to be uploaded
          blobImageIndexes.push(index);
        } else {
          // For existing images, extract just the filename
          const urlParts = imageUrl.split('/');
          const filename = urlParts[urlParts.length - 1];
          
          // Check if this filename exists in the original images
          if (originalImageFilenames.includes(filename)) {
            keptImageFilenames.push(filename);
          } else {
            // If we can't find an exact match, try fuzzy matching
            for (const origFilename of originalImageFilenames) {
              if (imageUrl.includes(origFilename)) {
                keptImageFilenames.push(origFilename);
                break;
              }
            }
          }
        }
      });
      
      console.log("Original image filenames:", originalImageFilenames);
      console.log("Kept image filenames:", keptImageFilenames);
      console.log("Blob image indexes:", blobImageIndexes);
      
      // STEP 3: Handle image deletion by explicitly setting the images field
      if (originalImageFilenames.length > 0 && keptImageFilenames.length === 0 && blobImageIndexes.length === 0) {
        // We're deleting ALL images and not adding new ones
        console.log("CASE: Deleting all images");
        data.images = [];  // Empty array tells PocketBase to delete all files
      } else if (keptImageFilenames.length < originalImageFilenames.length) {
        // We're deleting SOME images - specify which ones to keep
        console.log("CASE: Keeping some images");
        data.images = keptImageFilenames;
      }
      
      // STEP 4: Update the product data first
      let updatedProduct = await pb.collection('products').update(selectedProduct.id, data);
      console.log("Updated product data:", updatedProduct);
      
      // STEP 5: Handle new image uploads and/or digital product file if needed
      if (blobImageIndexes.length > 0 || (editForm.digital_product && digitalProductFile)) {
        console.log("CASE: Uploading new files");
        const fileFormData = new FormData();
        
        // Add the image files to upload
        blobImageIndexes.forEach(index => {
          const fileIndex = imageFiles.findIndex((_, i) => {
            // Try to match the blob URL index with the file index
            const blobCount = productImages.slice(0, index).filter(url => url.startsWith('blob:')).length;
            return i === blobCount;
          });
          
          if (fileIndex >= 0) {
            fileFormData.append('images', imageFiles[fileIndex]);
          }
        });
        
        // If we're keeping existing images, tell PocketBase
        if (keptImageFilenames.length > 0) {
          fileFormData.append('images+', JSON.stringify(keptImageFilenames));
        }
        
        // Add digital product file if applicable
        if (editForm.digital_product && digitalProductFile) {
          fileFormData.append('product', digitalProductFile);
        } else if (editForm.digital_product && !digitalProductFile && !editForm.download_link && selectedProduct.product) {
          // Keep the existing product file if not being replaced
          fileFormData.append('product+', selectedProduct.product);
        }
        
        // Upload the new files
        updatedProduct = await pb.collection('products').update(selectedProduct.id, fileFormData);
        console.log("Updated product with new files:", updatedProduct);
      }
      
      // Update product in state
      setProducts(prevProducts => 
        prevProducts.map(product => 
          product.id === selectedProduct.id ? updatedProduct : product
        )
      );
      
      // Update selected product if view modal is open
      if (viewModalOpen) {
        setSelectedProduct(updatedProduct);
      }
      
      setSnackbar({
        open: true,
        message: 'Product updated successfully',
        severity: 'success'
      });
      
      setEditModalOpen(false);
    } catch (error) {
      console.error('Error updating product:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to update product',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'BDT',
    }).format(amount);
  };
  
  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  
  // Get category name by ID
  const getCategoryName = (categoryId) => {
    return categories.find(cat => cat.id === categoryId)?.name || 'Unknown';
  };
  
  // Get seller name by ID
  const getSellerName = (sellerId) => {
    return sellers.find(seller => seller.id === sellerId)?.name || 'Unknown';
  };
  
  // Get shop name by seller ID
  const getShopName = (sellerId) => {
    return sellers.find(seller => seller.id === sellerId)?.shop || 'Unknown';
  };
  
  // Get product image URL (in a real app, would use PocketBase file URL)
  const getProductImageUrl = (product, index = 0) => {
    if (!product?.images?.[index]) {
      console.error('Product image not found at index', index);
      return ''; // or return a default image URL
    }
    console.log(pb.files.getURL(product, product.images[index]));

    return pb.files.getURL(product, product.images[index]);
  };
  
  // Apply status filter directly
  const handleStatusFilterClick = (status) => {
    setFilters(prev => ({
      ...prev,
      status
    }));
    setPage(0);
  };
  
  // Get discount percentage
  const getDiscountPercentage = (price, comparePrice) => {
    if (!comparePrice || comparePrice <= price) return null;
    return Math.round(((comparePrice - price) / comparePrice) * 100);
  };

  // Function to export products to CSV
const exportToCSV = () => {
  const csvData = products.map(product => {
    // Get the main image URL if available
    const imageUrl = product.images && product.images.length > 0 
      ? pb.files.getURL(product, product.images[0])
      : '';
      
    return {
      ID: product.id,
      Title: product.title,
      Description: product.description,
      Price: product.price,
      Stock: product.stock,
      Status: product.status,
      Category: getCategoryName(product.category),
      Seller: getSellerName(product.seller),
      ImageURL: imageUrl
    };
  });

  const csvContent = [
    Object.keys(csvData[0]).join(','),
    ...csvData.map(row => Object.values(row).map(value => {
      // Handle values with commas by wrapping in quotes
      if (value && typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
        // Replace double quotes with two double quotes to escape them
        const escaped = value.replace(/"/g, '""');
        return `"${escaped}"`;
      }
      return value;
    }).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, 'products.csv');
};
  
  // Function to export products to Excel
  const exportToExcel = () => {
    const excelData = products.map(product => {
      // Get the main image URL if available
      const imageUrl = product.images && product.images.length > 0 
        ? pb.files.getURL(product, product.images[0])
        : '';
        
      return {
        ID: product.id,
        Title: product.title,
        Description: product.description,
        Price: product.price,
        Stock: product.stock,
        Status: product.status,
        Category: getCategoryName(product.category),
        Seller: getSellerName(product.seller),
        ImageURL: imageUrl
      };
    });
  
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Products');
  
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'products.xlsx');
  };
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
            <Typography 
              color="inherit" 
              variant="body2"
              component={RouterLink}
              to="/dashboard"
              sx={{ textDecoration: 'none', color: 'text.secondary' }}
            >
              Dashboard
            </Typography>
            <Typography color="text.primary" variant="body2">Products</Typography>
          </Breadcrumbs>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Products
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your marketplace products, update inventory and track performance
          </Typography>
        </Box>
        {/* <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ 
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            textTransform: 'none',
          }}
          onClick={() => {
            setSelectedProduct(null);
            setEditForm({
              title: '',
              description: '',
              price: '',
              compare_price: '',
              stock: '',
              status: 'draft',
              featured: false,
              delivery_charge: '',
              seller: '',
              category: '',
              tags: [],
              variants: []
            });
            setProductImages([]);
            setImageFiles([]);
            setVideoUrl('');
            setFormErrors({});
            setEditModalOpen(true);
          }}
        >
          Add New Product
        </Button> */}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard>
            <StatIconWrapper color={blue[500]}>
              <Inventory fontSize="medium" />
            </StatIconWrapper>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {statistics.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Products
              </Typography>
            </Box>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard>
            <StatIconWrapper color={green[500]}>
              <Public fontSize="medium" />
            </StatIconWrapper>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {statistics.published}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Published
              </Typography>
            </Box>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard>
            <StatIconWrapper color={orange[500]}>
              <Edit fontSize="medium" />
            </StatIconWrapper>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {statistics.draft}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Draft
              </Typography>
            </Box>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard>
            <StatIconWrapper color={grey[500]}>
              <Delete fontSize="medium" />
            </StatIconWrapper>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {statistics.archived}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Archived
              </Typography>
            </Box>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard>
            <StatIconWrapper color={red[500]}>
              <Warning fontSize="medium" />
            </StatIconWrapper>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {statistics.outOfStock}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Out of Stock
              </Typography>
            </Box>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard>
            <StatIconWrapper color={orange[700]}>
              <Star fontSize="medium" />
            </StatIconWrapper>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {statistics.featured}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Featured
              </Typography>
            </Box>
          </StatCard>
        </Grid>
      </Grid>

      {/* Quick status filters */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <FilterButton 
          size="small" 
          startIcon={<Inventory />}
          onClick={() => handleStatusFilterClick('all')}
          active={filters.status === 'all' ? 1 : 0}
        >
          All Products
        </FilterButton>
        <FilterButton 
          size="small" 
          startIcon={<Public />}
          onClick={() => handleStatusFilterClick('published')}
          active={filters.status === 'published' ? 1 : 0}
        >
          Published
        </FilterButton>
        <FilterButton 
          size="small" 
          startIcon={<Edit />}
          onClick={() => handleStatusFilterClick('draft')}
          active={filters.status === 'draft' ? 1 : 0}
        >
          Draft
        </FilterButton>
        <FilterButton 
          size="small" 
          startIcon={<Delete />}
          onClick={() => handleStatusFilterClick('archived')}
          active={filters.status === 'archived' ? 1 : 0}
        >
          Archived
        </FilterButton>
        <FilterButton 
          size="small" 
          startIcon={<Warning />}
          onClick={() => handleStatusFilterClick('out_of_stock')}
          active={filters.status === 'out_of_stock' ? 1 : 0}
        >
          Out of Stock
        </FilterButton>
      </Box>

      {/* Search and filters */}
      <Paper sx={{ mb: 3, p: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              placeholder="Search products by name, description, or seller..."
              variant="outlined"
              value={searchQuery}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={handleFilterClick}
                size="small"
                sx={{ textTransform: 'none' }}
                color={activeFilterCount > 0 ? "primary" : "inherit"}
              >
                {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : 'Filter'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => {
                  setSearchQuery('');
                  setFilters({ 
                    status: 'all', 
                    seller: 'all', 
                    category: 'all',
                    sortBy: 'updated_desc'
                  });
                  fetchProducts();
                }}
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Reset
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                size="small"
                sx={{ textTransform: 'none' }}
                onClick={exportToCSV}
              >
                Export to CSV
              </Button>
              <Button
                variant="outlined"
                startIcon={<Upload />}
                size="small"
                sx={{ textTransform: 'none' }}
                onClick={exportToExcel}
              >
                Export to Excel
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        PaperProps={{
          elevation: 2,
          sx: { width: 280, maxWidth: '100%', borderRadius: 2 }
        }}
      >
        <MenuItem disabled>
          <Typography variant="subtitle2" fontWeight={600}>
            Filter by Status
          </Typography>
        </MenuItem>
        <MenuItem 
          selected={filters.status === 'all'} 
          onClick={() => handleFilterChange('status', 'all')}
        >
          All Products
        </MenuItem>
        <MenuItem 
          selected={filters.status === 'published'} 
          onClick={() => handleFilterChange('status', 'published')}
        >
          Published Products
        </MenuItem>
        <MenuItem 
          selected={filters.status === 'draft'} 
          onClick={() => handleFilterChange('status', 'draft')}
        >
          Draft Products
        </MenuItem>
        <MenuItem 
          selected={filters.status === 'archived'} 
          onClick={() => handleFilterChange('status', 'archived')}
        >
          Archived Products
        </MenuItem>
        <MenuItem 
          selected={filters.status === 'out_of_stock'} 
          onClick={() => handleFilterChange('status', 'out_of_stock')}
        >
          Out of Stock Products
        </MenuItem>
        
        <Divider sx={{ my: 1 }} />
        
        <MenuItem disabled>
          <Typography variant="subtitle2" fontWeight={600}>
            Filter by Category
          </Typography>
        </MenuItem>
        <MenuItem 
          selected={filters.category === 'all'} 
          onClick={() => handleFilterChange('category', 'all')}
        >
          All Categories
        </MenuItem>
        {categories.map((category) => (
          <MenuItem 
            key={category.id}
            selected={filters.category === category.id} 
            onClick={() => handleFilterChange('category', category.id)}
          >
            {category.name}
          </MenuItem>
        ))}
        
        <Divider sx={{ my: 1 }} />
        
        <MenuItem disabled>
          <Typography variant="subtitle2" fontWeight={600}>
            Filter by Seller
          </Typography>
        </MenuItem>
        <MenuItem 
          selected={filters.seller === 'all'} 
          onClick={() => handleFilterChange('seller', 'all')}
        >
          All Sellers
        </MenuItem>
        {sellers.map((seller) => (
          <MenuItem 
            key={seller.id}
            selected={filters.seller === seller.id} 
            onClick={() => handleFilterChange('seller', seller.id)}
          >
            {seller.shop_name}
          </MenuItem>
        ))}
        
        <Divider sx={{ my: 1 }} />
        
        <MenuItem disabled>
          <Typography variant="subtitle2" fontWeight={600}>
            Sort By
          </Typography>
        </MenuItem>
        <MenuItem 
          selected={filters.sortBy === 'updated_desc'} 
          onClick={() => handleFilterChange('sortBy', 'updated_desc')}
        >
          Recently Updated
        </MenuItem>
        <MenuItem 
          selected={filters.sortBy === 'updated_asc'} 
          onClick={() => handleFilterChange('sortBy', 'updated_asc')}
        >
          Oldest Updated
        </MenuItem>
        <MenuItem 
          selected={filters.sortBy === 'price_desc'} 
          onClick={() => handleFilterChange('sortBy', 'price_desc')}
        >
          Price: High to Low
        </MenuItem>
        <MenuItem 
          selected={filters.sortBy === 'price_asc'} 
          onClick={() => handleFilterChange('sortBy', 'price_asc')}
        >
          Price: Low to High
        </MenuItem>
        <MenuItem 
          selected={filters.sortBy === 'name_asc'} 
          onClick={() => handleFilterChange('sortBy', 'name_asc')}
        >
          Name: A to Z
        </MenuItem>
        <MenuItem 
          selected={filters.sortBy === 'name_desc'} 
          onClick={() => handleFilterChange('sortBy', 'name_desc')}
        >
          Name: Z to A
        </MenuItem>
      </Menu>

      {/* Products Table */}
      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
          <TableContainer>
            <Table sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Product</StyledTableCell>
                  <StyledTableCell>Category</StyledTableCell>
                  <StyledTableCell align="right">Price</StyledTableCell>
                  <StyledTableCell align="right">Stock</StyledTableCell>
                  <StyledTableCell>Seller</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell align="right">Updated</StyledTableCell>
                  <StyledTableCell align="center">Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading products...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography variant="body1">
                        No products found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Try adjusting your search or filter to find what you're looking for
                      </Typography>
                      {/* <Button
                        variant="outlined"
                        startIcon={<Add />}
                        sx={{ mt: 2, textTransform: 'none' }}
                        onClick={() => {
                          setSelectedProduct(null);
                          setEditForm({
                            title: '',
                            description: '',
                            price: '',
                            compare_price: '',
                            stock: '',
                            status: 'draft',
                            featured: false,
                            delivery_charge: '',
                            seller: '',
                            category: '',
                            tags: [],
                            variants: []
                          });
                          setProductImages([]);
                          setImageFiles([]);
                          setVideoUrl('');
                          setFormErrors({});
                          setEditModalOpen(true);
                        }}
                      >
                        Add New Product
                      </Button> */}
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((product) => {
                    const category = getCategoryData(product.category);
                    const discountPercent = getDiscountPercentage(product.price, product.compare_price);
                    
                    return (
                      <StyledTableRow 
                        key={product.id}
                        onClick={() => handleRowClick(product)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <ProductImage 
                              sx={{ 
                                backgroundImage: `url(${getProductImageUrl(product)})`,
                              }}
                            >
                              {!product.images || product.images.length === 0 && (
                                <ImageIcon color="action" sx={{ opacity: 0.5 }} />
                              )}
                            </ProductImage>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {product.title}
                                {product.featured && (
                                  <Chip 
                                    size="small" 
                                    label="Featured" 
                                    color="warning" 
                                    sx={{ ml: 1, height: 18, '& .MuiChip-label': { px: 1, py: 0.1 } }} 
                                  />
                                )}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                SKU: {product.id.substring(0, 8)}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={category.name} 
                            size="small" 
                            sx={{ 
                              bgcolor: 'background.paper',
                              border: 1,
                              borderColor: 'divider'
                            }} 
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {formatCurrency(product.price)}
                            </Typography>
                            {product.compare_price && (
                              <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ textDecoration: 'line-through', display: 'block' }}
                              >
                                {formatCurrency(product.compare_price)}
                              </Typography>
                            )}
                            {discountPercent && (
                              <Chip 
                                label={`-${discountPercent}%`} 
                                size="small" 
                                color="error" 
                                sx={{ 
                                  height: 18, 
                                  '& .MuiChip-label': { px: 0.8, py: 0 },
                                  fontWeight: 600,
                                  fontSize: '0.7rem'
                                }} 
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2"
                            color={product.stock === 0 ? 'error.main' : (product.stock < 10 ? 'warning.main' : 'inherit')}
                            fontWeight={product.stock === 0 || product.stock < 10 ? 600 : 400}
                          >
                            {product.stock === 0 ? 'Out of stock' : product.stock}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {product.shop}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            size="small"
                            status={product.status}
                            label={product.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatDate(product.updated)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Tooltip title="View Details">
                              <IconButton 
                                size="small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowClick(product);
                                }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Product">
                              <IconButton 
                                size="small" 
                                onClick={(e) => handleEditClick(e, product)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="More Actions">
                              <IconButton 
                                size="small" 
                                onClick={(e) => handleActionMenuOpen(e, product.id)}
                              >
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </StyledTableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={totalProducts}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={handleActionMenuClose}
        PaperProps={{
          elevation: 2,
          sx: { width: 200, borderRadius: 2 }
        }}
      >
        {currentProductId && (
          <>
            <MenuItem onClick={() => {
              const product = products.find(p => p.id === currentProductId);
              handleRowClick(product);
              handleActionMenuClose();
            }}>
              <ListItemIcon>
                <Visibility fontSize="small" />
              </ListItemIcon>
              <Typography variant="body2">View Details</Typography>
            </MenuItem>
            <MenuItem onClick={(e) => {
              const product = products.find(p => p.id === currentProductId);
              handleEditClick(e, product);
              handleActionMenuClose();
            }}>
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              <Typography variant="body2">Edit Product</Typography>
            </MenuItem>
            
            <Divider />
            
            {/* Status actions */}
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                Change Status
              </Typography>
            </MenuItem>
            {(() => {
              const product = products.find(p => p.id === currentProductId);
              if (!product) return null;
              
              return (
                <>
                  {product.status !== 'published' && (
                    <MenuItem onClick={() => handleStatusChange(currentProductId, 'published')}>
                      <ListItemIcon>
                        <Public fontSize="small" color="success" />
                      </ListItemIcon>
                      <Typography variant="body2">Publish</Typography>
                    </MenuItem>
                  )}
                  {product.status !== 'draft' && (
                    <MenuItem onClick={() => handleStatusChange(currentProductId, 'draft')}>
                      <ListItemIcon>
                        <Edit fontSize="small" color="warning" />
                      </ListItemIcon>
                      <Typography variant="body2">Set as Draft</Typography>
                    </MenuItem>
                  )}
                  {product.status !== 'archived' && (
                    <MenuItem onClick={() => handleStatusChange(currentProductId, 'archived')}>
                      <ListItemIcon>
                        <Delete fontSize="small" color="inherit" />
                      </ListItemIcon>
                      <Typography variant="body2">Archive</Typography>
                    </MenuItem>
                  )}
                  {product.status !== 'out_of_stock' && (
                    <MenuItem onClick={() => handleStatusChange(currentProductId, 'out_of_stock')}>
                      <ListItemIcon>
                        <Warning fontSize="small" color="error" />
                      </ListItemIcon>
                      <Typography variant="body2">Mark Out of Stock</Typography>
                    </MenuItem>
                  )}
                </>
              );
            })()}
            
            <Divider />
            
            {/* Featured action */}
            {(() => {
              const product = products.find(p => p.id === currentProductId);
              if (!product) return null;
              
              return (
                <MenuItem onClick={() => handleFeaturedToggle(currentProductId, product.featured)}>
                  <ListItemIcon>
                    <Star fontSize="small" color={product.featured ? "inherit" : "warning"} />
                  </ListItemIcon>
                  <Typography variant="body2">
                    {product.featured ? "Remove Featured" : "Mark as Featured"}
                  </Typography>
                </MenuItem>
              );
            })()}
            
            <Divider />
            
            {/* Delete action */}
            <MenuItem onClick={() => handleDeleteProduct(currentProductId)}>
              <ListItemIcon>
                <DeleteOutline fontSize="small" color="error" />
              </ListItemIcon>
              <Typography variant="body2" color="error">Delete Product</Typography>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* View Product Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        {selectedProduct && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={600}>
                  Product Details
                </Typography>
                <Box>
                  <Tooltip title="Edit Product">
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        setViewModalOpen(false);
                        handleEditClick(e, selectedProduct);
                      }}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    edge="end"
                    onClick={() => setViewModalOpen(false)}
                  >
                    <Close />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>
            
            <DialogContent dividers sx={{ p: 3 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
              >
                <Tab label="Basic Info" value={0} />
                <Tab label="Media" value={1} />
                <Tab label="Reviews" value={2} />
              </Tabs>
              
              {/* Basic Info Tab */}
              {tabValue === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%', borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Product Information
                        </Typography>
                        
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Product Name
                          </Typography>
                          <Typography variant="body1" fontWeight={500} gutterBottom>
                            {selectedProduct.title}
                            {selectedProduct.featured && (
                              <Chip 
                                size="small" 
                                label="Featured" 
                                color="warning" 
                                sx={{ ml: 1, height: 18, '& .MuiChip-label': { px: 1, py: 0.1 } }} 
                              />
                            )}
                          </Typography>
                          
                          <Divider sx={{ my: 1 }} />
                          
                          <Typography variant="body2" color="text.secondary">
                            Status
                          </Typography>
                          <Box sx={{ my: 0.5 }}>
                            <StatusChip
                              size="small"
                              status={selectedProduct.status}
                              label={selectedProduct.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            />
                          </Box>
                          
                          <Divider sx={{ my: 1 }} />
                          
                          <Typography variant="body2" color="text.secondary">
                            Category
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {getCategoryData(selectedProduct.category).name}
                          </Typography>
                          
                          <Divider sx={{ my: 1 }} />
                          
                          <Typography variant="body2" color="text.secondary">
                            Description
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {selectedProduct.description}
                          </Typography>
                          
                         
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={3} direction="column">
                      <Grid item>
                        <Card sx={{ borderRadius: 2 }}>
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                              Pricing and Inventory
                            </Typography>
                            
                            <Box sx={{ mt: 2 }}>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Typography variant="body2" color="text.secondary">
                                    Current Price
                                  </Typography>
                                  <Typography variant="h6" fontWeight={600} color="primary" gutterBottom>
                                    {formatCurrency(selectedProduct.price)}
                                  </Typography>
                                </Grid>
                                
                                <Grid item xs={6}>
                                  <Typography variant="body2" color="text.secondary">
                                    Original Price
                                  </Typography>
                                  <Typography 
                                    variant="body1" 
                                    color="text.secondary" 
                                    gutterBottom
                                    sx={selectedProduct.compare_price ? { textDecoration: 'line-through' } : {}}
                                  >
                                    {selectedProduct.compare_price 
                                      ? formatCurrency(selectedProduct.compare_price) 
                                      : 'N/A'}
                                  </Typography>
                                </Grid>
                                
                                {selectedProduct.compare_price && (
                                  <Grid item xs={12}>
                                    <Chip 
                                      label={`Save ${getDiscountPercentage(selectedProduct.price, selectedProduct.compare_price)}%`} 
                                      size="small" 
                                      color="error"
                                      sx={{ fontWeight: 600 }}
                                    />
                                  </Grid>
                                )}
                                
                                <Grid item xs={6}>
                                  <Typography variant="body2" color="text.secondary">
                                    Stock
                                  </Typography>
                                  <Typography 
                                    variant="body1" 
                                    fontWeight={500}
                                    color={selectedProduct.stock === 0 
                                      ? 'error.main' 
                                      : (selectedProduct.stock < 10 ? 'warning.main' : 'inherit')}
                                    gutterBottom
                                  >
                                    {selectedProduct.stock === 0 
                                      ? 'Out of Stock' 
                                      : `${selectedProduct.stock} units`}
                                  </Typography>
                                </Grid>
                                
                                <Grid item xs={6}>
                                  <Typography variant="body2" color="text.secondary">
                                    Delivery Charge
                                  </Typography>
                                  <Typography variant="body1" fontWeight={500} gutterBottom>
                                    {selectedProduct.delivery_charge 
                                      ? formatCurrency(selectedProduct.delivery_charge) 
                                      : 'Free'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      {selectedProduct.digital_product && (
                        <Grid item>
                          <Card sx={{ borderRadius: 2, bgcolor: 'background.default' }}>
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TextSnippet color="primary" sx={{ mr: 1 }} />
                                <Typography variant="subtitle1" fontWeight={600}>
                                  Digital Product
                                </Typography>
                                <Chip 
                                  size="small" 
                                  label="Digital" 
                                  color="primary" 
                                  sx={{ ml: 1, height: 20 }}
                                />
                              </Box>
                              
                              <Divider sx={{ my: 1 }} />
                              
                              <Box sx={{ mt: 2 }}>
                                {selectedProduct.product && (
                                  <>
                                    <Typography variant="body2" color="text.secondary">
                                      Product File
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, mb: 2 }}>
                                      <TextSnippet fontSize="small" color="primary" sx={{ mr: 1 }} />
                                      <Typography variant="body1" fontWeight={500}>
                                        {selectedProduct.product}
                                      </Typography>
                                    </Box>
                                  </>
                                )}
                                
                                {selectedProduct.download_link && (
                                  <>
                                    <Typography variant="body2" color="text.secondary">
                                      Download Link
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                      <Link fontSize="small" color="primary" sx={{ mr: 1 }} />
                                      <Typography 
                                        variant="body1" 
                                        fontWeight={500}
                                        component="a"
                                        href={selectedProduct.download_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ color: 'primary.main', textDecoration: 'none' }}
                                      >
                                        {selectedProduct.download_link}
                                      </Typography>
                                    </Box>
                                  </>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      )}
                      
                      <Grid item>
                        <Card sx={{ borderRadius: 2 }}>
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                              Seller Information
                            </Typography>
                            
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Shop Name
                              </Typography>
                              <Typography variant="body1" fontWeight={500} gutterBottom>
                                {selectedProduct.shop}
                              </Typography>
                              
                              <Divider sx={{ my: 1 }} />
                              
                              <Typography variant="body2" color="text.secondary">
                                Seller Name
                              </Typography>
                              <Typography variant="body1" gutterBottom>
                                {getSellerName(selectedProduct.seller)}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                      
                      <Grid item>
                        <Card sx={{ borderRadius: 2 }}>
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                              System Information
                            </Typography>
                            
                            <Box sx={{ mt: 2 }}>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Typography variant="body2" color="text.secondary">
                                    Product ID
                                  </Typography>
                                  <Typography variant="body2" fontFamily="monospace" gutterBottom>
                                    {selectedProduct.id}
                                  </Typography>
                                </Grid>
                                
                                <Grid item xs={6}>
                                  <Typography variant="body2" color="text.secondary">
                                    Slug
                                  </Typography>
                                  <Typography variant="body2" fontFamily="monospace" gutterBottom>
                                    {selectedProduct.slug}
                                  </Typography>
                                </Grid>
                                
                                <Grid item xs={6}>
                                  <Typography variant="body2" color="text.secondary">
                                    Created
                                  </Typography>
                                  <Typography variant="body2" gutterBottom>
                                    {formatDate(selectedProduct.created)}
                                  </Typography>
                                </Grid>
                                
                                <Grid item xs={6}>
                                  <Typography variant="body2" color="text.secondary">
                                    Last Updated
                                  </Typography>
                                  <Typography variant="body2" gutterBottom>
                                    {formatDate(selectedProduct.updated)}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              
              {/* Media Tab */}
              {tabValue === 1 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Product Images
                        </Typography>
                        
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          {selectedProduct.images && selectedProduct.images.length > 0 ? (
                            selectedProduct.images.map((image, index) => (
                              <Grid item xs={6} sm={4} key={index}>
                                <ImagePreview
                                  sx={{
                                    backgroundImage: `url(${pb.files.getURL(selectedProduct, image)})`,
                                    width: '100px',
                                  }}
                                >
                                  <ImageActions className="image-actions">
                                    <IconButton size="small" sx={{ color: 'white' }}>
                                      <RemoveRedEye />
                                    </IconButton>
                                  </ImageActions>
                                </ImagePreview>
                              </Grid>
                            ))
                          ) : (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                No images available
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Product Video
                        </Typography>
                        
                        {selectedProduct.video ? (
                          <Box sx={{ mt: 2 }}>
                            <Paper 
                              sx={{ 
                                p: 2, 
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                bgcolor: 'action.hover'
                              }}
                            >
                              <VideocamOutlined color="primary" />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight={500}>
                                  Product Video
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  component="div" 
                                  sx={{ 
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {selectedProduct.video}
                                </Typography>
                              </Box>
                              <IconButton size="small">
                                <RemoveRedEye fontSize="small" />
                              </IconButton>
                            </Paper>
                          </Box>
                        ) : (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              No video available
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
               
              
              
              {/* Reviews Tab */}
              {tabValue === 2 && (
                <Card sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Customer Reviews
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i}
                              fontSize="small"
                              sx={{ 
                                color: i < Math.floor(selectedProduct.rating) ? 'warning.main' : 'action.disabled',
                                fontSize: '1rem'
                              }}
                            />
                          ))}
                        </Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {selectedProduct.rating.toFixed(1)}/5.0
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          ({selectedProduct.reviews_count} reviews)
                        </Typography>
                      </Box>
                    </Box>
                    
                    {selectedProduct.reviews && selectedProduct.reviews.length > 0 ? (
                      <List sx={{ p: 0 }}>
                        {Array.from({ length: Math.min(5, selectedProduct.reviews.length) }, (_, i) => (
                          <React.Fragment key={i}>
                            <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                              <ListItemAvatar>
                                <Avatar 
                                  src={`https://i.pravatar.cc/150?u=${selectedProduct.id}_reviewer_${i}`}
                                  alt={`Reviewer ${i+1}`}
                                />
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle2" fontWeight={500}>
                                      Customer {i+1}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      {[...Array(5)].map((_, j) => (
                                        <Star 
                                          key={j}
                                          fontSize="small"
                                          sx={{ 
                                            color: j < Math.floor(Math.random() * 3) + 3 ? 'warning.main' : 'action.disabled',
                                            fontSize: '0.8rem'
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                }
                                secondary={
                                  <>
                                    <Typography component="span" variant="body2" color="text.primary">
                                      Great product! Exactly as described and arrived quickly.
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                      {formatDate(new Date(Date.now() - Math.random() * 10000000000).toISOString())}
                                    </Typography>
                                  </>
                                }
                              />
                            </ListItem>
                            {i < Math.min(4, selectedProduct.reviews.length - 1) && <Divider variant="inset" component="li" />}
                          </React.Fragment>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No reviews yet
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}
            </DialogContent>
            
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button 
                variant="outlined" 
                onClick={() => setViewModalOpen(false)}
                sx={{ textTransform: 'none' }}
              >
                Close
              </Button>
              <Button 
                variant="contained" 
                startIcon={<Edit />}
                onClick={(e) => {
                  setViewModalOpen(false);
                  handleEditClick(e, selectedProduct);
                }}
                sx={{ textTransform: 'none' }}
              >
                Edit Product
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Product Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => !isSubmitting && setEditModalOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              {selectedProduct ? 'Edit Product' : 'Add New Product'}
            </Typography>
            <IconButton
              edge="end"
              onClick={() => !isSubmitting && setEditModalOpen(false)}
              disabled={isSubmitting}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
          >
            <Tab label="Basic Info" value={0} />
            <Tab label="Media" value={1} />
          </Tabs>
          
          {/* Basic Info Tab */}
          {tabValue === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Basic Information
                    </Typography>
                    
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        fullWidth
                        label="Product Title"
                        name="title"
                        value={editForm.title}
                        onChange={handleInputChange}
                        error={Boolean(formErrors.title)}
                        helperText={formErrors.title}
                        sx={{ mb: 3 }}
                        required
                      />
                      
                      <TextField
                        fullWidth
                        label="Product Description"
                        name="description"
                        value={editForm.description}
                        onChange={handleInputChange}
                        multiline
                        rows={4}
                        error={Boolean(formErrors.description)}
                        helperText={formErrors.description}
                        sx={{ mb: 3 }}
                        required
                      />
                      
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth required error={Boolean(formErrors.category)}>
                            <InputLabel>Category</InputLabel>
                            <Select
                              name="category"
                              value={editForm.category}
                              onChange={handleInputChange}
                              label="Category"
                              sx={{ minWidth: 200 }}
                            >
                              {categories.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                  {category.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {formErrors.category && (
                              <Typography variant="caption" color="error">
                                {formErrors.category}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth required error={Boolean(formErrors.seller)}>
                            <InputLabel>Seller</InputLabel>
                            <Select
                              name="seller"
                              value={editForm.seller}
                              onChange={handleInputChange}
                              label="Seller"
                              sx={{ minWidth: 200 }}
                            >
                              {sellers.map((seller) => (
                                <MenuItem key={seller.id} value={seller.id}>
                                  {seller.shop_name}
                                </MenuItem>
                              ))}
                            </Select>
                            {formErrors.seller && (
                              <Typography variant="caption" color="error">
                                {formErrors.seller}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                      </Grid>
                      
                   
                      
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Grid container spacing={3} direction="column">
                  <Grid item>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Pricing
                        </Typography>
                        
                        <Box sx={{ mt: 2 }}>
                          <TextField
                            fullWidth
                            label="Price"
                            name="price"
                            value={editForm.price}
                            onChange={handleInputChange}
                            type="number"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AttachMoney />
                                </InputAdornment>
                              ),
                            }}
                            error={Boolean(formErrors.price)}
                            helperText={formErrors.price}
                            sx={{ mb: 3 }}
                            required
                          />
                          
                          <TextField
                            fullWidth
                            label="Compare Price"
                            name="compare_price"
                            value={editForm.compare_price}
                            onChange={handleInputChange}
                            type="number"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AttachMoney />
                                </InputAdornment>
                              ),
                            }}
                            error={Boolean(formErrors.compare_price)}
                            helperText={formErrors.compare_price || "Leave empty if there's no sale price"}
                            sx={{ mb: 3 }}
                          />
                          
                          <TextField
                            fullWidth
                            label="Delivery Charge"
                            name="delivery_charge"
                            value={editForm.delivery_charge}
                            onChange={handleInputChange}
                            type="number"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocalShipping fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                            sx={{ mb: 3 }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Inventory
                        </Typography>
                        
                        <Box sx={{ mt: 2 }}>
                          <TextField
                            fullWidth
                            label="Stock Quantity"
                            name="stock"
                            value={editForm.stock}
                            onChange={handleInputChange}
                            type="number"
                            error={Boolean(formErrors.stock)}
                            helperText={formErrors.stock}
                            sx={{ mb: 3 }}
                            required
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Product Status
                        </Typography>
                        
                        <Box sx={{ mt: 2 }}>
                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                              name="status"
                              value={editForm.status}
                              onChange={handleInputChange}
                              label="Status"
                            >
                              <MenuItem value="draft">Draft</MenuItem>
                              <MenuItem value="published">Published</MenuItem>
                              <MenuItem value="archived">Archived</MenuItem>
                              <MenuItem value="out_of_stock">Out of Stock</MenuItem>
                            </Select>
                          </FormControl>
                          
                          <FormControlLabel
                            control={
                              <Switch
                                checked={editForm.featured}
                                onChange={handleInputChange}
                                name="featured"
                                color="warning"
                              />
                            }
                            label="Feature this product"
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item>
                    <Card sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Digital Product
                        </Typography>
                        
                        <Box sx={{ mt: 2 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={editForm.digital_product}
                                onChange={handleInputChange}
                                name="digital_product"
                                color="primary"
                              />
                            }
                            label="This is a digital product"
                          />
                          
                          {editForm.digital_product && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Digital Product Delivery
                              </Typography>
                              
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                Choose one of the following options to deliver your digital product
                              </Typography>
                              
                              {formErrors.digital_product && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                  {formErrors.digital_product}
                                </Alert>
                              )}
                              
                              <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Option 1: Upload Digital Product File
                                </Typography>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                  <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<Upload />}
                                    size="small"
                                    disabled={Boolean(editForm.download_link)}
                                  >
                                    Select File
                                    <input
                                      type="file"
                                      hidden
                                      onChange={handleDigitalProductUpload}
                                    />
                                  </Button>
                                  
                                  {digitalProductFileName && (
                                    <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
                                      <TextSnippet fontSize="small" color="primary" sx={{ mr: 1 }} />
                                      <Typography variant="body2" sx={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {digitalProductFileName}
                                      </Typography>
                                      <IconButton size="small" onClick={handleClearDigitalProduct}>
                                        <Close fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  )}
                                  
                                  {selectedProduct?.product && !digitalProductFile && (
                                    <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
                                      <TextSnippet fontSize="small" color="primary" sx={{ mr: 1 }} />
                                      <Typography variant="body2">
                                        Current file: {selectedProduct.product}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </Paper>
                              
                              <Typography variant="body2" sx={{ textAlign: 'center', my: 1 }}>OR</Typography>
                              
                              <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Option 2: External Download Link
                                </Typography>
                                
                                <TextField
                                  fullWidth
                                  label="Download Link"
                                  name="download_link"
                                  value={editForm.download_link}
                                  onChange={handleDownloadLinkChange}
                                  placeholder="https://example.com/download/product.zip"
                                  disabled={Boolean(digitalProductFile)}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <Link />
                                      </InputAdornment>
                                    ),
                                  }}
                                  helperText="Provide a direct download link to your digital product"
                                />
                              </Paper>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
          
          {/* Media Tab */}
          {tabValue === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Product Images
                    </Typography>
                    
                    <Box 
                      sx={{ mt: 2 }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragging(true);
                      }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleImageDrop}
                    >
                      <UploadZone
                        isDragging={isDragging}
                        onClick={() => document.getElementById('product-images').click()}
                      >
                        <input
                          type="file"
                          id="product-images"
                          multiple
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={handleImageUpload}
                        />
                        <Upload color="action" sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="subtitle1" fontWeight={500}>
                          Drop images here or click to upload
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Drag and drop product images or click to select files
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                          Supported formats: JPEG, PNG, WebP, AVIF
                        </Typography>
                        {formErrors.images && (
                          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                            {formErrors.images}
                          </Typography>
                        )}
                      </UploadZone>
                      
                      {productImages.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Uploaded Images ({productImages.length})
                          </Typography>
                          
                          <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="product-images" direction="horizontal">
                              {(provided) => (
                                <Grid 
                                  container 
                                  spacing={2} 
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                >
                                  {productImages.map((image, index) => (
                                    <Draggable key={index} draggableId={`image-${index}`} index={index}>
                                      {(provided) => (
                                        <Grid 
                                          item 
                                          xs={6} 
                                          sm={4} 
                                          md={3}
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                        >
                                          <Paper
                                            elevation={2}
                                            sx={{ borderRadius: 2, overflow: 'hidden', position: 'relative' }}
                                          >
                                            <ImagePreview
                                              sx={{
                                                backgroundImage: `url(${image})`,
                                                height: 120
                                              }}
                                            />
                                            <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <IconButton 
                                                  size="small" 
                                                  {...provided.dragHandleProps}
                                                  sx={{ cursor: 'grab' }}
                                                >
                                                  <DragIndicator fontSize="small" />
                                                </IconButton>
                                                <Typography variant="caption" color="text.secondary">
                                                  {index === 0 ? 'Main' : `#${index + 1}`}
                                                </Typography>
                                              </Box>
                                              <IconButton 
                                                size="small" 
                                                color="error"
                                                onClick={() => handleImageDelete(index)}
                                              >
                                                <DeleteOutline fontSize="small" />
                                              </IconButton>
                                            </Box>
                                          </Paper>
                                        </Grid>
                                      )}
                                    </Draggable>
                                  ))}
                                  {provided.placeholder}
                                </Grid>
                              )}
                            </Droppable>
                          </DragDropContext>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Product Video (Optional)
                    </Typography>
                    
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        fullWidth
                        label="Video URL"
                        placeholder="https://example.com/video.mp4"
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <VideocamOutlined />
                            </InputAdornment>
                          ),
                        }}
                        helperText="Add a YouTube, Vimeo, or direct video URL"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
          
  
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            variant="outlined" 
            onClick={() => setEditModalOpen(false)}
            disabled={isSubmitting}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Save />}
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{ textTransform: 'none' }}
          >
            {isSubmitting ? 'Saving...' : (selectedProduct ? 'Save Changes' : 'Create Product')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Products;